import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { 
  Check, 
  ArrowRight, 
  SkipForward, 
  Phone, 
  Database, 
  Bot, 
  Sparkles,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [fubApiKey, setFubApiKey] = useState('');
  const [retellApiKey, setRetellApiKey] = useState('');
  const [retellAgentId, setRetellAgentId] = useState('');
  const [loading, setLoading] = useState(false);

  const totalSteps = 5;

  const handleSkip = () => {
    navigate('/dashboard');
    toast.info('You can complete setup later in Settings');
  };

  const handleNext = async () => {
    if (currentStep === 2 && fubApiKey) {
      // Validate and save FUB API key
      setLoading(true);
      try {
        await axios.post(
          `${BACKEND_URL}/api/user/integrations/fub`,
          { api_key: fubApiKey },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        toast.success('Follow Up Boss connected successfully!');
      } catch (error) {
        toast.error('Invalid FUB API key. Please check and try again.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    if (currentStep === 3 && retellApiKey) {
      // Validate and save Retell API key
      setLoading(true);
      try {
        await axios.post(
          `${BACKEND_URL}/api/user/integrations/retell`,
          { api_key: retellApiKey, agent_id: retellAgentId },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        toast.success('Retell AI connected successfully!');
      } catch (error) {
        toast.error('Invalid Retell API key. Please check and try again.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    if (currentStep === totalSteps) {
      // Mark onboarding as complete
      try {
        await axios.post(
          `${BACKEND_URL}/api/user/complete-onboarding`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        navigate('/dashboard');
        toast.success('Setup complete! Welcome to PropFlo 🎉');
      } catch (error) {
        navigate('/dashboard');
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 2) return fubApiKey.length > 0;
    if (currentStep === 3) return retellApiKey.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[...Array(totalSteps)].map((_, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      index + 1 < currentStep
                        ? 'bg-blue-600 text-white'
                        : index + 1 === currentStep
                        ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {index + 1 < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <span className="text-xs mt-2 text-gray-900 font-semibold">
                    Step {index + 1}
                  </span>
                </div>
                {index < totalSteps - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded transition-all ${
                      index + 1 < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <Card className="p-8 bg-white shadow-xl border border-gray-200">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-blue-600 rounded-2xl mx-auto flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Welcome to PropFlo! 🎉</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                You're about to transform your real estate lead qualification with AI-powered automation.
                Let's get you set up in just a few steps!
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-3">What we'll set up:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Connect your Follow Up Boss CRM</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Set up Retell AI voice agent</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Configure your AI assistant (Jason)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Create your first automation workflow</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                ⏱️ Takes about 5 minutes • You can skip and configure later
              </p>
            </div>
          )}

          {/* Step 2: Connect FUB */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <Database className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Follow Up Boss</h2>
                <p className="text-gray-600">
                  Sync your leads automatically to your Follow Up Boss CRM
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
                <p className="font-semibold mb-2">How to get your FUB API Key:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Log in to Follow Up Boss</li>
                  <li>Go to Settings → Integrations → API</li>
                  <li>Click "Create API Key"</li>
                  <li>Copy the key and paste it below</li>
                </ol>
                <a
                  href="https://docs.followupboss.com/reference/authentication"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2 font-medium"
                >
                  View detailed guide <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Follow Up Boss API Key
                </label>
                <Input
                  type="password"
                  value={fubApiKey}
                  onChange={(e) => setFubApiKey(e.target.value)}
                  placeholder="fka_xxxxxxxxxxxxxxxxxxxxxxx"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Your API key is encrypted and stored securely. We never share your credentials.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Connect Retell AI */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Retell AI</h2>
                <p className="text-gray-600">
                  Enable AI-powered voice calls for instant lead qualification
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
                <p className="font-semibold mb-2">How to get your Retell AI credentials:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Sign up at <a href="https://app.retellai.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">app.retellai.com</a></li>
                  <li>Go to Settings → API Keys</li>
                  <li>Generate new API key</li>
                  <li>Copy your Agent ID from Agents page</li>
                  <li>Paste both below</li>
                </ol>
                <a
                  href="https://docs.retellai.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2 font-medium"
                >
                  View Retell AI docs <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Retell API Key
                  </label>
                  <Input
                    type="password"
                    value={retellApiKey}
                    onChange={(e) => setRetellApiKey(e.target.value)}
                    placeholder="key_xxxxxxxxxxxxxxxxxxxxxxx"
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Agent ID (Optional)
                  </label>
                  <Input
                    type="text"
                    value={retellAgentId}
                    onChange={(e) => setRetellAgentId(e.target.value)}
                    placeholder="agent_xxxxxxxxxxxxxxx"
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If you already have a Retell agent configured, enter the ID. Otherwise, we'll help you create one.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Configure Agent */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <Bot className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Meet Jason - Your AI Assistant</h2>
                <p className="text-gray-600">
                  Jason will handle voice calls, SMS, and email to qualify your leads 24/7
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Jason's Capabilities:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Voice Calls</h4>
                      <p className="text-sm text-gray-600">Natural conversations with prospects</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Lead Scoring</h4>
                      <p className="text-sm text-gray-600">HOT/WARM/COLD qualification</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Database className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">CRM Sync</h4>
                      <p className="text-sm text-gray-600">Auto-sync to Follow Up Boss</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Smart Follow-ups</h4>
                      <p className="text-sm text-gray-600">Automated email & SMS sequences</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">✨ Good news!</strong> Jason is pre-configured with our proven 
                  real estate qualification flow. You can customize his behavior anytime in Settings.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-600 rounded-2xl mx-auto flex items-center justify-center">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">All Set! 🎉</h2>
              <p className="text-lg text-gray-600">
                Your PropFlo account is ready to qualify leads and close more deals.
              </p>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-4">What's Next:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">1</span>
                    </div>
                    <span>View your dashboard and explore the platform</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">2</span>
                    </div>
                    <span>Import your existing leads or let Jason start qualifying new ones</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">3</span>
                    </div>
                    <span>Check your Follow Up Boss dashboard to see synced leads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">4</span>
                    </div>
                    <span>Customize automation workflows and templates</span>
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>14-Day Free Trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>24/7 Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Cancel Anytime</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between gap-4">
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Skip for now
            </Button>

            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                'Processing...'
              ) : currentStep === totalSteps ? (
                'Go to Dashboard'
              ) : (
                <>
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Need help? <a href="mailto:support@aipropflo.com" className="text-blue-600 hover:text-blue-700 font-medium">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
